import {
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined
} from "@ant-design/icons"
import { Button, Dropdown, type MenuProps, notification } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { exportDashboardPdf, exportDashboardXlsx } from "../../api/services"
import type { GlobalFilters, PortfolioWindow } from "../../redux/portfolio.types"
import { downloadBlob, filenameFromContentDisposition } from "../../../../utils/download-file"

interface ExportButtonsProps {
  window: PortfolioWindow
  filters?: GlobalFilters
  selectedPreset?: PortfolioWindow["preset"]
  teamScope?: string[]
}

type ServiceFn = (
  w: PortfolioWindow,
  f?: GlobalFilters,
  teamScope?: string[]
) => Promise<{ ok: boolean; data?: unknown; headers?: Record<string, string> }>

const isWindowExportable = (
  window: PortfolioWindow,
  selectedPreset = window.preset
) =>
  selectedPreset !== "custom" ||
  (window.preset === "custom" && Boolean(window.start && window.end))

// Exports are blob requests, so a JSON error body arrives as a Blob and has to be
// read back before the server's own message can be shown.
const serverMessage = async (data: unknown): Promise<string | undefined> => {
  if (!(data instanceof Blob)) return undefined
  try {
    return JSON.parse(await data.text())?.details?.message
  } catch {
    return undefined
  }
}

export const ExportButtons = ({
  window,
  filters,
  selectedPreset,
  teamScope
}: ExportButtonsProps) => {
  const { t } = useTranslation()
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [loadingXlsx, setLoadingXlsx] = useState(false)
  const disabled = !isWindowExportable(window, selectedPreset)
  const loading = loadingPdf || loadingXlsx

  const runExport = async (
    serviceFn: ServiceFn,
    ext: string,
    setLoading: (v: boolean) => void
  ) => {
    if (disabled) return

    setLoading(true)
    try {
      const response = await serviceFn(window, filters, teamScope)
      if (!response.ok) {
        notification.error({
          message:
            (await serverMessage(response.data)) || t("portfolio.export.failed")
        })
        return
      }
      if (response.data) {
        const filename =
          filenameFromContentDisposition(response.headers) ?? `dashboard-${window.preset}.${ext}`
        downloadBlob(response.data as Blob, filename)
      }
    } catch {
      notification.error({ message: t("portfolio.export.failed") })
    } finally {
      setLoading(false)
    }
  }

  const items: MenuProps["items"] = [
    {
      key: "pdf",
      icon: <FilePdfOutlined />,
      label: t("portfolio.export.pdf", "Export PDF"),
      onClick: () => runExport(exportDashboardPdf, "pdf", setLoadingPdf)
    },
    {
      key: "xlsx",
      icon: <FileExcelOutlined />,
      label: t("portfolio.export.excel", "Export Excel"),
      onClick: () => runExport(exportDashboardXlsx, "xlsx", setLoadingXlsx)
    }
  ]

  return (
    <Dropdown menu={{ items }} trigger={["click"]} disabled={disabled}>
      <Button
        icon={<DownloadOutlined />}
        loading={loading}
        disabled={disabled}
        aria-label={t("portfolio.export.label", "Export")}
      >
        {t("portfolio.export.label", "Export")}
      </Button>
    </Dropdown>
  )
}
